import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap } from 'rxjs/operators';
import * as LockerActions from './locker.actions';
import { ApiService } from '../../services/api.service';
import { AnyObject, Auction, AuctionItem, MyLockerItem } from '../../types';
import { Action } from '@ngrx/store';
import * as ItemActions from '../item/item.actions';
import * as AuctionActions from '../auction/auction.actions';
import { AlertService } from '@fan-shared/alert/alert.service';

@Injectable()
export class LockerEffects {
  alertService = inject(AlertService);
  processMyLocker$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LockerActions.pullLocker),
      mergeMap(({ section }) => this.pullMyLocker(section, 0))
    );
  });

  processMyLockerNextPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LockerActions.pullLockerNextPage),
      mergeMap(({ section, skip, limit }) => this.pullMyLocker(section, skip, limit))
    );
  });

  private pullMyLocker(section: 'live' | 'won' | 'lost', skip?: number, limit?: number) {
    return this.api.locker.myLocker(section, skip, limit).pipe(
      mergeMap(({ bids, nextPage }) => {
        const myBids: MyLockerItem[] = bids.map((bid) => ({
          id: bid.id,
          bidId: bid.bidId,
          auctionItemId: bid.auctionItemId,
          bidType: bid.bidType,
          purchaseCount: 'purchaseCount' in bid ? bid.purchaseCount : undefined,
          fulfillmentOption: 'fulfillmentOption' in bid ? bid.fulfillmentOption : undefined,
          customMessage: 'customMessage' in bid ? bid.customMessage : undefined,
          auction: bid.auction ? bid.auction : undefined,
          auctionItem: bid.auctionItem ? bid.auctionItem : undefined,
          foundation: bid.foundation,
          bid: 'bid' in bid ? bid.bid : undefined,
        }));
        const items = bids.map((b) => b.auctionItem).filter((b) => b) as AuctionItem[];
        const auctions = bids.map((b) => b.auction).filter((b) => b) as Auction[];
        const res: Action<any>[] = [
          ...items.map((item) => ItemActions.upsertItem({ item })),
          ...auctions.map((auction) => AuctionActions.upsertAuction({ auction })),
          skip === 0
            ? LockerActions.pullLockerSucceeded({ bids: myBids, nextPage })
            : LockerActions.pullLockerNextPageSucceeded({ nextBids: myBids, nextPage }),
        ];
        return res;
      }),
      catchError((error) => this.onError(error))
    );
  }

  private onError(err: AnyObject) {
    const message = err?.error?.error?.message ?? 'There was an error processing the transaction';
    this.alertService.open(message);
    const res: Action<any>[] = [LockerActions.pullLockerFailed()];
    return res;
  }

  constructor(
    private actions$: Actions,
    private readonly api: ApiService
  ) {}
}
